import { Check, Video } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { meetingClock } from '../../../../lib/meeting-time';
import { meetingHasEnded, meetingStatus, meetingView, phase1State } from '../../../../lib/meeting-view';
import { meetingRowAction, meetingRowDecision } from '../../../../lib/meeting-row-state';
import { useMeetingsChannelActions, useMeetingsSchedule, type UpcomingMeeting } from '../../../../lib/meetings-channel';
import { useAgentName } from '../../../../lib/use-agent-name';
import { avatarInitials, avatarSlotFor } from '../../../../lib/avatar-color';
import { cardJoinOpen, cardMinutes, type CalendarItemCard, type CardJoinOpen, type CalendarItemCardBooking } from '../../../../lib/calendar/calendar-item-card';
import { useAwaitedBookingOutcome } from '../../../../lib/calendar/use-awaited-booking-outcome';
import { useLiveCardBooking } from '../../../../lib/calendar/use-live-card-booking';
import { queryClient, queryKeys } from '../../../../lib/query-client';
import { itemWhen, type CalendarFormatParts } from '../../../../lib/calendar/calendar-format';
import { BookingActions, CalendarActionToast, hasBookingActions, type CalendarActionNotice } from '../../calendar/BookingActions';
import { KIND_CLASS, KIND_ICON } from '../../calendar/calendar-kinds';
import { useCalendarFormat } from '../../calendar/use-calendar-format';
import { Avatar, Badge, Button, Card, Switch, TextButton, Toast } from '../../ui';
import { MeetingDecisionToast, useMeetingDecision } from '../../connectors/MeetingDecisionToast';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import calendarStyles from '../../calendar/Calendar.module.css';
import styles from './CalendarItemMessage.module.css';

// The calendar item the owner tapped, drawn in their own bubble in place of the seed text — the reply under it is
// where its current state comes. A booking the Calendar offered buttons for carries them here too, as its Next steps:
// the card stores only which booking it is (`bookingRef`), and the buttons follow that booking as read now. The same
// card is iOS `CalendarItemMessage` and Android's chat bubble.
export function CalendarItemMessage({ card }: { card: CalendarItemCard }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const format = useCalendarFormat(card.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const Icon = KIND_ICON[card.kind];
  const joinOpen = useJoinOpen(card);
  const booking = card.booking;
  return (
    <div className={styles.card} data-testid="calendarItemMessage">
      <span className={`sym-p3 ${styles.kind} ${calendarStyles[KIND_CLASS[card.kind]]}`}>
        <Icon size={16} aria-hidden />
        {t(`calendar.card.kind.${card.kind}`)}
      </span>
      <span dir="auto" className={`sym-type-small-medium ${styles.title}`}>
        {card.title}
      </span>
      {card.tags && (
        <span className={styles.tags}>
          {card.tags.map((tag) => (
            <Badge key={tag.label} variant={tag.variant} label={tag.label} />
          ))}
        </span>
      )}
      {booking ? (
        <BookingLines card={card} booking={booking} parts={format.parts} />
      ) : (
        <>
          <span className={`sym-p3 ${styles.meta}`}>{itemWhen(format.parts, card)}</span>
          {card.meta && (
            <span dir="auto" className={`sym-p3 ${styles.meta}`}>
              {card.meta}
            </span>
          )}
        </>
      )}
      {((joinOpen.call && card.joinUrl) || card.meetingEventId) && (
        <span className={styles.actions}>
          {joinOpen.call && card.joinUrl && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Video size={16} aria-hidden />}
              onClick={() => {
                logPageClickBi({ page_name: 'chat', button_name: 'calendar_card_join' });
                window.open(card.joinUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              {t('calendar.card.joinCall')}
            </Button>
          )}
          {card.meetingEventId && <MaestroJoin eventId={card.meetingEventId} windowOpen={joinOpen.decision} />}
        </span>
      )}
      {card.bookingRef && <NextSteps card={card} timeZone={format.timeZone} time={format.time} />}
    </div>
  );
}

/**
 * The booking's own buttons, as the Calendar's details card offers them and through the same prepare and confirm, decided
 * from the booking as read now. Nothing shows while it is read, or when it cannot be read or is gone. After a change here
 * or its Undo, the buttons wait for the booking to be read again, and the changed ones until a read shows the change.
 */
function NextSteps({ card, timeZone, time }: { card: CalendarItemCard; timeZone: string; time: (at: number) => string }) {
  const { t } = useTranslation();
  const agentName = useAgentName();
  const { logClickCta } = useGenericBi();
  const live = useLiveCardBooking(card);
  const [now, setNow] = useState(() => Date.now());
  const [actedAt, setActedAt] = useState<number>();
  const [notice, setNotice] = useState<CalendarActionNotice>();
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  // After an action the card waits for the next read of the booking; a read that FAILED after it ends the wait too, so
  // the buttons come back from the last good read (less those the action changed) with a way to read again.
  const refreshFailed = actedAt !== undefined && live.failedAt > actedAt && live.failedAt >= live.readAt;
  const current = actedAt === undefined || live.readAt > actedAt || refreshFailed;
  const ref = card.kind === 'booking' ? card.bookingRef : undefined;
  const awaitedOutcome = useAwaitedBookingOutcome(live.item, ref?.siteId, ref?.bookingId, now);
  const reread = () => {
    setActedAt(Date.now());
    void queryClient.invalidateQueries({ queryKey: queryKeys.calendar });
  };
  return (
    <>
      {live.item && current && hasBookingActions(live.item, now, awaitedOutcome) && (
        <div className={styles.nextSteps}>
          <span className="sym-type-small-medium">{t('calendar.nextSteps.title')}</span>
          <BookingActions
            item={live.item}
            timeZone={timeZone}
            time={time}
            onActed={(result) => {
              if (result.severity === 'success') reread();
              setNotice(result);
            }}
          />
          <span className={`sym-p3 ${styles.meta} ${styles.wrap}`}>{t('calendar.nextSteps.note', { maestroAgentName: agentName.name })}</span>
        </div>
      )}
      {refreshFailed && (
        <span role="alert" className={`sym-p3 ${styles.refreshFailed}`}>
          {t('calendar.nextSteps.refreshFailed')}
          <TextButton
            size="tiny"
            onClick={() => {
              logClickCta('calendar-card-reread');
              live.reread();
            }}
          >
            {t('calendar.notice.retry')}
          </TextButton>
        </span>
      )}
      <CalendarActionToast
        notice={notice}
        onClose={() => setNotice(undefined)}
        onChanged={(result) => {
          if (result.severity === 'success') reread();
          setNotice(result);
        }}
      />
    </>
  );
}

/** The booking as the "Appointment detail" design draws it, without its buttons: Maestro's reply carries the actions. */
function BookingLines({ card, booking, parts }: { card: CalendarItemCard; booking: CalendarItemCardBooking; parts: CalendarFormatParts }) {
  const { t } = useTranslation();
  const minutes = cardMinutes(card);
  const when = [
    itemWhen(parts, card),
    ...(minutes ? [t('meetings.duration.minutes', { count: minutes })] : []),
    ...(booking.staff ? [t('calendar.card.withStaff', { names: booking.staff.join(', ') })] : []),
  ].join(' · ');
  const price = booking.price ? parts.price(booking.price.amount, booking.price.currency) : undefined;
  const payment = booking.payment ? t(PAYMENT_KEY[booking.payment]) : undefined;
  return (
    <>
      <span dir="auto" className={`sym-p3 ${styles.meta} ${styles.wrap}`}>
        {when}
      </span>
      {booking.places && <span className={`sym-p3 ${styles.meta}`}>{t('calendar.item.places', booking.places)}</span>}
      {booking.clients?.map((name) => (
        <Card key={name} variant="stone" size="sm" className={styles.client}>
          <Avatar initials={avatarInitials(name)} size={24} color={avatarSlotFor(name)} />
          <span dir="auto" className={`sym-type-small-medium ${styles.title}`}>
            {name}
          </span>
        </Card>
      ))}
      {(price || payment) && <span className={`sym-p3 ${styles.meta}`}>{[price, payment].filter(Boolean).join(' · ')}</span>}
      {booking.site && (
        <span dir="auto" className={`sym-p3 ${styles.meta}`}>
          {booking.site}
        </span>
      )}
    </>
  );
}

export const PAYMENT_KEY = {
  paid: 'calendar.booking.paid',
  not_paid: 'calendar.booking.notPaid',
  partially_paid: 'calendar.booking.partlyPaid',
  refunded: 'calendar.booking.refunded',
} as const;

/** The card is drawn once, so the clock it judges the join window by ticks while the window can still open or close. */
function useJoinOpen(card: CalendarItemCard): CardJoinOpen {
  const joinable = card.kind === 'meeting' && !!(card.joinUrl || card.meetingEventId);
  const [now, setNow] = useState(() => Date.now());
  const ended = now >= card.end;
  useEffect(() => {
    if (!joinable || ended) return;
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, [joinable, ended]);
  return joinable ? cardJoinOpen(card, now) : CLOSED;
}

const CLOSED: CardJoinOpen = { call: false, decision: false };

/**
 * Whether Maestro attends the meeting, read live from the Meetings channel and changed through its own action.
 * `windowOpen` is the chat card's decision window (until the meeting ends): a row without `meetingState` shows its
 * buttons only inside it, while a phase 1 row also shows how the meeting went after it.
 */
export function MaestroJoin({
  eventId,
  windowOpen = true,
  biPage = 'chat',
}: {
  eventId: string;
  windowOpen?: boolean;
  /** page_name for its clicks: the chat card and the calendar's details both render this. */
  biPage?: 'chat' | 'calendar';
}) {
  const { schedule } = useMeetingsSchedule();
  // The schedule lists the calendar's video meetings even when the Meetings channel is not connected.
  if (!schedule?.connection) return null;
  const meeting = schedule.meetings.find((each) => each.calendarEventId === eventId);
  if (!meeting) return null;
  if (phase1State(meeting)) return <MaestroJoinToggle meeting={meeting} biPage={biPage} />;
  return windowOpen ? <MaestroJoinButtons meeting={meeting} biPage={biPage} /> : null;
}

/**
 * The calendar card's toggle (design "Maestro on meetings v4", after the Netta sync): "{agent} joins the call", with
 * the "Notes will be taken" chip while on. Turning it on is an Add, off is a Remove (the 5 s Undo only during the meeting); during the
 * meeting off takes her out of the call. Once the meeting is over, or when the owner declined it, the row has no
 * switch and says how it went. Rendered from `meetingState` (`lib/meeting-view.ts`).
 */
function MaestroJoinToggle({ meeting, biPage }: { meeting: UpcomingMeeting; biPage: 'chat' | 'calendar' }) {
  const { t, i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const agentName = useAgentName();
  const decision = useMeetingDecision();
  const view = meetingView(meeting);
  const status = meetingStatus(meeting, t, agentName.name);
  const state = phase1State(meeting);
  const params = { maestroAgentName: agentName.name, agent: agentName.name };
  const on = state === 'will_join' || state === 'in_meeting';
  const toggleable = state === 'not_added' || state === 'will_join' || state === 'in_meeting';
  const hint = status.note && (
    <span role={status.note.alert ? 'alert' : undefined} className={`sym-p3 ${styles.meta} ${styles.wrap}`}>
      {status.note.text}
    </span>
  );

  // Past its end, a row the server has not yet moved to an after-meeting state is stale: no switch, no state.
  if (toggleable && meetingHasEnded(meeting, Date.now())) return null;

  if (!toggleable || view.hint === 'connectors.meetings.hint.declined') {
    return (
      <span className={styles.maestroDone}>
        {status.badge && <Badge size="small" variant={status.badge.variant} label={status.badge.label} />}
        {hint}
      </span>
    );
  }

  const description = on ? (
    <span className={styles.maestroOn}>
      <Badge size="tiny" variant="done" startIcon={<Check size={12} aria-hidden />} label={t('calendar.card.notesWillBeTaken')} />
      {state === 'in_meeting'
        ? t('calendar.card.maestroInMeetingHelp', params)
        : view.hint
        ? t(view.hint, params)
        : t('calendar.card.joinsAt', { time: meetingClock(Date.parse(meeting.startsAt), i18n.language || undefined) })}
    </span>
  ) : (
    t('calendar.card.maestroOffHelp')
  );

  return (
    <Card component="span" variant="stone" size="sm" className={styles.maestroRow}>
      <Switch
        checked={on}
        disabled={decision.busy || view.action === 'none'}
        label={t('calendar.card.maestroJoinsCall', params)}
        description={description}
        onChange={() => {
          if (view.action === 'none') return;
          logPageClickBi({
            page_name: biPage,
            button_name: `calendar_card_maestro_${view.action}`,
            location: biPage === 'calendar' ? 'calendar_details' : 'chat_card',
          });
          decision.send(meeting, view.action === 'add' ? 'join' : 'remove');
        }}
      />
      {status.note?.alert && hint}
      <MeetingDecisionToast decision={decision} biPage={biPage} />
    </Card>
  );
}

/** A row without `meetingState` (a site the TypeScript harness serves): Send / Don't send / Undo skip, as before. */
function MaestroJoinButtons({ meeting, biPage }: { meeting: UpcomingMeeting; biPage: 'chat' | 'calendar' }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const agentName = useAgentName();
  const { decide } = useMeetingsChannelActions();
  const [failed, setFailed] = useState(false);
  const action = meetingRowAction(meeting.state);
  if (action === 'none') return null;
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disabled={decide.isLoading}
        onClick={() => {
          logPageClickBi({
            page_name: biPage,
            button_name: `calendar_card_maestro_${action}`,
            location: biPage === 'calendar' ? 'calendar_details' : 'chat_card',
          });
          decide.mutate(
            { meeting, decision: meetingRowDecision(action) },
            { onSuccess: (answer) => answer.kind !== 'ok' && setFailed(true), onError: () => setFailed(true) },
          );
        }}
      >
        {t(MAESTRO_JOIN_LABEL[action], { maestroAgentName: agentName.name })}
      </Button>
      <Toast open={failed} message={t('common.error.generic')} severity="error" onClose={() => setFailed(false)} />
    </>
  );
}

const MAESTRO_JOIN_LABEL = { join: 'calendar.card.sendMaestro', skip: 'calendar.card.dontSendMaestro', undo: 'calendar.card.undoSkip' } as const;
